/**
 * MainContent is a flexible component for displaying a virtualized table with advanced features such as search, filtering, sorting, CSV export, and dynamic footers.
 * It serves as the main area for data tables, integrating action controls, tabbed filters, custom table actions, and modal management.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {Object} props.page - Table configuration, including columns, data, and default sorting.
 * @param {Object} [props.filterOptions] - Options for client-side filters.
 * @param {Object} [props.filterDbOptions] - Options for server-side filters.
 * @param {Function} [props.setNoDbFilters] - Setter for client-side filter values.
 * @param {Object} [props.tabsOptions] - Tab configuration, including active tab and filter values.
 * @param {Object} [props.tableOptions] - Table options, such as footer calculation.
 * @param {Object} [props.tableActions] - Custom table actions.
 * @param {Object} [props.btnCrtModalOptions] - Options for the create modal button.
 * @returns {JSX.Element} The rendered content with action controls and a virtualized table.
 *
 * @example
 * <MainContent
 *   page={{
 *     columns: [
 *       { dataField: 'name', text: 'Name' },
 *       { dataField: 'age', text: 'Age', footer: values => values.reduce((a, b) => a + b, 0) }
 *     ],
 *     data: [{ name: 'Alice', age: 30 }, { name: 'Bob', age: 25 }],
 *     defaultSorted: [{ dataField: 'name', order: 'asc' }]
 *   }}
 *   filterOptions={{ name: ['Alice', 'Bob'] }}
 *   tabsOptions={{ activeTab: 'all', filterValues: { all: { name: 'Alice' } } }}
 *   tableOptions={{ calculateFooter: true }}
 *   tableActions={{ customAction: () => alert('Action!') }}
 *   btnCrtModalOptions={{ show: true, onClick: () => {} }}
 * />
 */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import { AvForm, AvField } from "availity-reactstrap-validation";
import { VirtualizedTable } from "../../components/Table/VirtualizedTable";
import { ActionRow } from "../../components/Table/ActionRow";
import { getScreenHeight } from "../../helpers/screen_height_helper";
import { InfoPopover } from "../../pages/Priority/InfoPopover";

export const MainContent = ({
  page,
  notificationSettings = null,
  myOrdersOptions = null,
  filterOptions = null,
  filterDbOptions = null,
  setNoDbFilters = null,
  tabsOptions = null,
  tableOptions = null,
  tableActions = null,
  btnCrtModalOptions = null,
  btnCrtScndModalOptions = null,
  btnCrtThrdModalOptions = null,
  infoPopoverOptions = null,
}) => {
  const { pg, defaultSorted, selectRow, columns, data, key = null, props = {}, rowClasses, filter, cellEdit } = page;
  const { globalActionOptions, globalActionBtnLbl, globalActionExtraContent, handleGlobalActionSubmit, selectedRows } = tableActions || {};
  const { activeTab, filterValues } = tabsOptions || {};
  const { calculateFooter } = tableOptions || {};
  const [searchText, setSearchText] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const formRef = useRef();

  const isBootstrap = useMemo(() => {
    if (!page) return false;
    return !(page.pg === "fees" || page.data.length >= 20 || page.data.length === 0);
  }, [page]);

  useEffect(() => {
    if (!isBootstrap) return;

    const handler = () => {
      let section = document.getElementById("card-section");
      // if(page && page.pg === 'notifications') {
      //   section = document.getElementById('card-notification'); // make sure id matches
      // } else {
      //   section = document.getElementById('card-section');
      // }
      // console.log('Section height adjustment for bootstrap table:', section);
      const table = document.querySelector(".react-bootstrap-table");
      if (section && table) {
        const h = section.offsetHeight;
        table.style.height = `calc(${h}px - 220px)`;
        table.style.overflow = "auto";
      }
    };

    // 🔹 Fire when this branch first appears
    // (next tick in case the inner DOM is still settling)
    const t = setTimeout(handler, 0);

    // 🔹 Keep old listeners
    window.addEventListener("resize", handler);
    window.addEventListener("load", handler, { once: true });

    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", handler);
      window.removeEventListener("load", handler);
    };
    // Re-run when the branch toggles on, or the data length changes
  }, [isBootstrap, page?.data?.length]);

  /**
   * Initializes the sorting state based on the defaultSorted property from the page prop.
   * If defaultSorted is provided and contains at least one sorting rule, sets the sortField and sortOrder
   * state variables to match the first sorting rule. This ensures the table is initially sorted as specified.
   *
   * @effect
   * @param {Array} defaultSorted - An array of sorting rules, where each rule contains a dataField and order.
   * @example
   * // If defaultSorted = [{ dataField: 'name', order: 'asc' }]
   * // Then sortField will be set to 'name' and sortOrder to 'asc'
   */
  useEffect(() => {
    if (defaultSorted && defaultSorted.length > 0) {
      setSortField(defaultSorted[0].dataField);
      setSortOrder(defaultSorted[0].order);
    }
  }, [defaultSorted]);
  /**
   * Generates a footer row object for a table based on the provided columns and data.
   * For each column, if a `footer` function is defined, it computes the column values from the data,
   * applies the footer function, and assigns the result to the corresponding field in the footer row.
   * Otherwise, it assigns an empty string to the field.
   * The returned object includes a special `_isFooter` property set to true.
   *
   * @param {Array<Object>} columns - The array of column definitions. Each column may have a `dataField` (string) and an optional `footer` function.
   * @param {Array<Object>} data - The array of data objects representing table rows.
   * @returns {Object} The generated footer row object, with computed footer values for each column and an `_isFooter` flag.
   */
  const getFooterRow = (columns, data) => {
    const footerRow = {};
    columns.forEach((column) => {
      if (typeof column.footer === "function") {
        const columnValues = data.map((row) => {
          const value = column.dataField.split(".").reduce((obj, key) => obj?.[key], row);
          return value ?? 0;
        });
        // Store JSX or raw value to be rendered differently
        footerRow[column.dataField] = column.footer(columnValues);
      } else {
        footerRow[column.dataField] = ""; // or null
      }
    });
    footerRow._isFooter = true;
    return footerRow;
  };
  /**
   * Processes table data by applying search, filters, and sorting.
   *
   * - Filters rows based on a search text across all columns.
   * - Applies column-specific filters, including support for date comparisons.
   * - Sorts the resulting data by a specified field and order.
   *
   * @function
   * @param {Array<Object>} data - The original array of data rows to process.
   * @returns {Array<Object>} The processed data after applying search, filters, and sorting.
   */
  const processData = useCallback(
    (data) => {
      let processedData = [...data];
      // Apply search
      if (searchText) {
        processedData = processedData.filter((row) =>
          columns.some((column) => {
            const value = column.dataField.split(".").reduce((obj, key) => obj?.[key], row);
            return value?.toString().toLowerCase().includes(searchText.toLowerCase());
          }),
        );
      }
      // Apply filters
      const tabFilters = filterValues?.[activeTab] || {};
      if (tabFilters && Object.keys(tabFilters).length > 0) {
        Object.entries(tabFilters).forEach(([field, value]) => {
          if (value) {
            processedData = processedData.filter((row) => {
              const cellValue = field.split(".").reduce((obj, key) => obj?.[key], row);
              if (typeof value === "object" && (value.date || value.comparator)) {
                const comparator = value.comparator || "=";
                const cellDate = new Date(cellValue.split(",")[0].split("/").reverse().join("-"));
                const filterDate = new Date(value.date);
                if (isNaN(cellDate) || isNaN(filterDate)) return true;
                switch (comparator) {
                  case "=":
                    return cellDate.toDateString() === filterDate.toDateString();
                  case ">":
                    return cellDate > filterDate;
                  case "<":
                    return cellDate < filterDate;
                  case ">=":
                    return cellDate >= filterDate;
                  case "<=":
                    return cellDate <= filterDate;
                  default:
                    return true;
                }
              } else {
                return cellValue?.toString().toLowerCase().includes(value.toLowerCase());
              }
              // return cellValue?.toString().toLowerCase().includes(value.toLowerCase());
            });
          }
        });
      }
      // Apply sorting
      if (sortField) {
        processedData.sort((a, b) => {
          const aValue = sortField.split(".").reduce((obj, key) => obj?.[key], a);
          const bValue = sortField.split(".").reduce((obj, key) => obj?.[key], b);
          if (aValue === bValue) return 0;
          if (sortOrder === "asc") {
            return aValue < bValue ? -1 : 1;
          } else {
            return aValue > bValue ? -1 : 1;
          }
        });
      }
      return processedData;
    },
    [sortField, sortOrder, searchText, filterValues, columns],
  );
  // Final data
  const processedData = processData(data);
  // Add footer to data if needed
  const footerRow = getFooterRow(columns, processedData);
  const virtualTableData = calculateFooter === true ? [...processedData, footerRow] : processedData;
  /**
   * Updates the search text state when the user types in the search box.
   * Triggers table data filtering based on the new search value.
   * @param {string} value - The user's search input.
   */
  const handleSearch = (value) => {
    setSearchText(value);
  };
  /**
   * Exports the current table data to a CSV file and triggers a download.
   * Processes the provided data and columns, applies optional CSV formatting,
   * handles nested data fields, and ensures proper CSV formatting (including
   * escaping values containing commas). The resulting CSV file is named 'export.csv'.
   * @function
   */
  const handleExportCSV = () => {
    const processedData = processData(data);
    const csvContent = [];
    // Add headers
    const headers = columns.filter((column) => column.csvExport !== false).map((column) => column.text);
    csvContent.push(headers.join(","));
    // Add data rows
    processedData.forEach((row) => {
      const rowData = columns
        .filter((column) => column.csvExport !== false)
        .map((column) => {
          let value = column.dataField.split(".").reduce((obj, key) => obj?.[key], row);

          // Use csvFormatter if available
          if (column.csvFormatter) {
            value = column.csvFormatter(value, row);
          }

          // Handle values that might contain commas
          if (typeof value === "string" && value.includes(",")) {
            value = `"${value}"`;
          }

          return value || "";
        });
      csvContent.push(rowData.join(","));
    });
    // Create and trigger download
    const blob = new Blob([csvContent.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "export.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const dataForBootstrap = useMemo(() => (isBootstrap && Array.isArray(data) ? data.map((r) => ({ ...r })) : data), [isBootstrap, data]);

  const handleGlobalSubmit = (e, values) => {
    handleGlobalActionSubmit(selectedRows, values);
    // Reset form fields to default
    // setSelectedRows([])
    formRef.current.reset();
  };

  return (
    <>
      <ActionRow
        page={page}
        notificationSettings={notificationSettings}
        processedData={virtualTableData}
        isBootstrap={isBootstrap}
        myOrdersOptions={myOrdersOptions}
        searchOptions={{ searchText, handleSearch }}
        handleExportCSV={handleExportCSV}
        filterOptions={filterOptions}
        filterDbOptions={filterDbOptions}
        setNoDbFilters={setNoDbFilters}
        btnCrtModalOptions={btnCrtModalOptions}
        btnCrtScndModalOptions={btnCrtScndModalOptions}
        btnCrtThrdModalOptions={btnCrtThrdModalOptions}
        infoPopoverOptions={infoPopoverOptions}
      />
      {/* {(page && (page.data.length >= 20 || page.data.length === 0 || (page.data.length < 20 && page.pg !== 'prices'))) ? ( */}
      {!isBootstrap ? (
        <VirtualizedTable
          page={page}
          tabsOptions={tabsOptions}
          tableOptions={tableOptions}
          processedData={virtualTableData} // filtered/sorted data
          sortingOptions={{ sortField, setSortField, sortOrder, setSortOrder }}
          tableActions={tableActions}
        />
      ) : (
        <>
          <BootstrapTable
            {...(props?.baseProps || {})}
            data={dataForBootstrap}
            {...(selectRow ? { selectRow } : {})}
            classes={"table align-middle table-nowrap "}
            headerClasses="header-class is-sticky-hdr"
            // filter={filter}
            // cellEdit={cellEdit}
            expandRow={page.expandRow}
            id="tableScroll"
            bordered={true}
            responsive
            {...(rowClasses ? { rowClasses } : {})}
            {...(filter ? { filter } : {})}
            {...(cellEdit ? { cellEdit } : {})}
          />
          {globalActionOptions && (
            <div className={selectedRows && selectedRows.length === 0 ? "action_row hidden" : "action_row"}>
              <AvForm ref={formRef} onValidSubmit={handleGlobalSubmit}>
                <AvField type="select" name="action" className="form-select">
                  <option value="SELECT ACTION">-- Select Action --</option>
                  {Array.isArray(globalActionOptions)
                    ? globalActionOptions.map((action, index) => (
                        <option key={index} value={action.value} className={`${action.className || ""} fw-bold fs-5`}>
                          {action.text}
                        </option>
                      ))
                    : Object.entries(globalActionOptions).map(([group, options]) => (
                        <optgroup key={group} label={group.charAt(0).toUpperCase() + group.slice(1)}>
                          {options.map((action, index) => (
                            <option key={`${group}-${index}`} value={action.value} className="fw-bold fs-5">
                              {action.text}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                </AvField>
                {pg !== "transfers" && globalActionExtraContent}
                <button className="btn btn-success"> {globalActionBtnLbl} </button>
                {pg === "transfers" && globalActionExtraContent}
              </AvForm>
              <div className="sidebar-background"></div>
            </div>
          )}
        </>
      )}
    </>
  );
};
